import { useState, useEffect, useMemo, useRef } from 'react';
import { getExpertiseTags, saveExpertiseTags } from '../../services/profileService';
import { BadgeCelebration } from '../BadgeCelebration/BadgeCelebration';
import { ServiceTile, Medallion } from '../awards/AwardBadge';
import {
  ACHIEVEMENT_BADGES,
  getUserBadges,
  awardBadge,
  resetBadges,
  getBadgesByCategory,
} from '../../services/gamificationService';
import { useAuth } from '../../hooks/useAuth';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { PageHeader } from '../ui/PageHeader';
import { SettingsCard, SettingRow, Toggle as SettingToggle, RadioRow } from './SettingsPrimitives';
import { PeopleAccess } from './PeopleAccess';
import { syncPhotosToBackend } from '../../services/photoUploadService';
import { loadConfig, saveConfig } from '../../hooks/useAppConfig';
import { SERVICE_AWARDS } from '../awards/serviceAwards';
import { AwardMetalsSettings } from './AwardMetalsSettings';

function UserIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function SlidersIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="4" y1="21" x2="4" y2="14" />
      <line x1="4" y1="10" x2="4" y2="3" />
      <line x1="12" y1="21" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12" y2="3" />
      <line x1="20" y1="21" x2="20" y2="16" />
      <line x1="20" y1="12" x2="20" y2="3" />
      <line x1="1" y1="14" x2="7" y2="14" />
      <line x1="9" y1="8" x2="15" y2="8" />
      <line x1="17" y1="16" x2="23" y2="16" />
    </svg>
  );
}

function AppConfiguration() {
  const [config, setConfig] = useState(loadConfig);

  const update = (key, value) => {
    setConfig((prev) => {
      const next = { ...prev, [key]: value };
      saveConfig(next);
      return next;
    });
  };

  const viewOptions = [
    { value: 'grid', label: 'Directory' },
    { value: 'department', label: 'Departments' },
    { value: 'orgchart', label: 'Org Chart' },
  ];

  const displayOptions = [
    { value: 'list', label: 'List' },
    { value: 'card', label: 'Card' },
  ];

  const featureToggles = [
    { key: 'showDepartments', label: 'Departments', description: 'Browse the roster grouped by department' },
    { key: 'showExpertise', label: 'Expertise', description: 'Browse skills and areas of expertise across the directory' },
    { key: 'showOrgChart', label: 'Org Chart', description: 'Visual organizational hierarchy view' },
    { key: 'showAnniversaries', label: 'MOREanniversaries', description: 'Shows a panel of this month\'s work anniversaries alongside the directory list' },
    { key: 'showFavorites', label: 'Favorites', description: 'Allow users to star and save employees' },
    { key: 'showRecentContacts', label: 'Recent Contacts', description: 'Track recently viewed employee profiles' },
    { key: 'showGrowth', label: 'Growth', description: 'Skills assessments, goals, manager reviews, and coaching check-ins' },
    { key: 'showScopeDirectReports', label: 'My Direct Reports', description: 'Sidebar shortcut scoping the directory to people who report to you' },
    { key: 'showScopeMyTeam', label: 'My Team', description: 'Sidebar shortcut scoping the directory to your manager\'s other reports' },
    { key: 'showScopeManagersTeam', label: "My Manager's Team", description: "Sidebar shortcut scoping the directory to your manager and their reports" },
    { key: 'showScopeMyDepartment', label: 'My Department', description: 'Sidebar shortcut scoping the directory to your department' },
    { key: 'showScopeMyOffice', label: 'My Office', description: 'Sidebar shortcut scoping the directory to your office' },
  ];

  return (
    <div className="space-y-3.5">
      <div>
        <h3 className="text-[16px] font-bold text-ink mb-[3px]">App configuration</h3>
        <p className="text-[12.5px] text-ink-3 leading-[1.5]">
          Defaults and feature availability for every user in the tenant. Changes take effect on their next page load.
        </p>
      </div>

      {/* .card — defaults for new users */}
      <SettingsCard
        title="Defaults for new users"
        description="What someone sees the first time they open PeopleVUE. Existing users keep their own choice."
      >
        <SettingRow
          label="Default view"
          help="The view the app opens on. Someone who changes it keeps their preference on that device."
        >
          <RadioRow
            name="defaultView"
            options={viewOptions}
            value={config.defaultView}
            onChange={(value) => update('defaultView', value)}
          />
        </SettingRow>
        <SettingRow
          label="Default display mode"
          help="List is denser and faster to scan; cards show larger photos."
        >
          <RadioRow
            name="defaultDisplayMode"
            options={displayOptions}
            value={config.defaultDisplayMode}
            onChange={(value) => update('defaultDisplayMode', value)}
          />
        </SettingRow>
      </SettingsCard>

      {/* .card.danger — turning a feature off removes it for all 739 people, so
          the container says so rather than relying on the toggle to. */}
      <SettingsCard
        title="Features"
        description="Availability for every user in the tenant. Turning one off hides it for everyone on their next page load."
        danger
      >
        {featureToggles.map(({ key, label, description }) => (
          <SettingRow key={key} label={label} help={description}>
            <SettingToggle
              checked={config[key]}
              onChange={(val) => update(key, val)}
              label={`${label} — ${config[key] ? 'on' : 'off'} for everyone`}
            />
          </SettingRow>
        ))}
      </SettingsCard>
    </div>
  );
}

function TagIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
      <line x1="7" y1="7" x2="7.01" y2="7" />
    </svg>
  );
}

function PlusIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

/**
 * A tag's holder count is the whole point of showing it here. Removing a tag
 * nobody holds is housekeeping; removing one 96 people hold takes a capability
 * out of the Expertise browser and the Filters facet for everyone. The old card
 * showed both as identical pills with an identical ✕.
 */
function CategoryCard({ category, onUpdate, onDelete, countsByTag }) {
  const [newTag, setNewTag] = useState('');
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState(category.name);
  const [pendingRemoveTag, setPendingRemoveTag] = useState(null);

  const addTag = () => {
    const trimmed = newTag.trim();
    if (!trimmed || category.tags.includes(trimmed)) { setNewTag(''); return; }
    onUpdate({ ...category, tags: [...category.tags, trimmed].sort() });
    setNewTag('');
  };

  const dropTag = (tag) => onUpdate({ ...category, tags: category.tags.filter((t) => t !== tag) });

  // Unheld tags go straight out; a held one names its blast radius first.
  const removeTag = (tag) => {
    if ((countsByTag?.get(tag) ?? 0) > 0) setPendingRemoveTag(tag);
    else dropTag(tag);
  };

  const commitName = () => {
    const trimmed = nameInput.trim();
    if (trimmed && trimmed !== category.name) onUpdate({ ...category, name: trimmed });
    setEditingName(false);
  };

  const totalTags = category.tags.length;

  return (
    <div className="bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-700 rounded-xl overflow-hidden">
      {/* Category header */}
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100 dark:border-zinc-700 bg-slate-50/60 dark:bg-zinc-800/60">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {editingName ? (
            <input
              type="text"
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') commitName(); if (e.key === 'Escape') { setNameInput(category.name); setEditingName(false); } }}
              onBlur={commitName}
              autoFocus
              className="flex-1 px-2 py-1 text-sm font-semibold text-slate-800 dark:text-zinc-100 border border-teal-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400/40 bg-white dark:bg-zinc-800"
            />
          ) : (
            <button
              onClick={() => { setNameInput(category.name); setEditingName(true); }}
              className="text-sm font-semibold text-slate-800 dark:text-zinc-100 hover:text-teal-600 dark:hover:text-teal-400 transition-colors text-left truncate"
              title="Click to rename"
            >
              {category.name}
            </button>
          )}
          <span className="text-xs text-slate-400 font-normal flex-shrink-0">{totalTags} tag{totalTags !== 1 ? 's' : ''}</span>
        </div>
        <button
          onClick={() => onDelete(category.id)}
          title="Delete category"
          className="ml-2 p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors flex-shrink-0"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      </div>

      {/* Tags */}
      <div className="p-4">
        <div className="flex flex-wrap gap-1.5 mb-3">
          {category.tags.map((tag) => {
            const held = countsByTag?.get(tag) ?? 0;
            return (
            <span
              key={tag}
              title={held ? `${held} ${held === 1 ? 'person holds' : 'people hold'} this tag` : 'Nobody holds this tag'}
              className={`inline-flex items-center gap-1.5 px-2.5 h-[26px] rounded-full text-[12px] font-semibold border
                ${held
                  ? 'bg-accent-soft text-accent border-accent-line'
                  : 'bg-surface-2 text-ink-3 border-line'}`}
            >
              {tag}
              <span className={`font-mono text-[11px] font-semibold ${held ? 'text-accent' : 'text-ink-3'}`}>{held}</span>
              <button
                onClick={() => removeTag(tag)}
                aria-label={`Remove ${tag}`}
                className="opacity-50 hover:opacity-100 transition-opacity ml-0.5">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </span>
            );
          })}
          {category.tags.length === 0 && (
            <p className="text-xs text-slate-400 italic py-1">No tags yet — add one below.</p>
          )}
        </div>

        {/* Add tag inline */}
        <div className="flex gap-2">
          <input
            type="text"
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addTag()}
            placeholder="Add a tag..."
            className="flex-1 px-3 py-1.5 text-xs border border-slate-200 dark:border-zinc-700 rounded-lg bg-white dark:bg-zinc-800 dark:text-zinc-100 dark:placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400"
          />
          <button
            onClick={addTag}
            disabled={!newTag.trim()}
            className="px-3 py-1.5 bg-teal-500 text-white rounded-lg text-xs font-medium hover:bg-teal-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Add
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={Boolean(pendingRemoveTag)}
        title={`Remove the "${pendingRemoveTag}" tag?`}
        body={`${countsByTag?.get(pendingRemoveTag) ?? 0} people hold this tag. It disappears from the Expertise browser and the skills facet in Filters for everyone. They keep it on their own profile, so re-adding the tag here brings the capability back.`}
        confirmLabel="Remove tag"
        onConfirm={() => { dropTag(pendingRemoveTag); setPendingRemoveTag(null); }}
        onCancel={() => setPendingRemoveTag(null)}
      />
    </div>
  );
}

function ExpertiseTags({ allSkills = [] }) {
  const [categories, setCategories] = useState([]);
  const [pendingDeleteCategory, setPendingDeleteCategory] = useState(null);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [showNewCategory, setShowNewCategory] = useState(false);

  useEffect(() => { setCategories(getExpertiseTags()); }, []);

  // Holders per tag, and the distinct people behind a whole category — one person
  // with four tags in a category is one person, not four.
  const countsByTag = useMemo(
    () => new Map(allSkills.map((s) => [s.skill, s.count])),
    [allSkills],
  );
  const peopleByTag = useMemo(
    () => new Map(allSkills.map((s) => [s.skill, s.people ?? []])),
    [allSkills],
  );
  const categoryHolders = (category) => {
    const ids = new Set();
    (category?.tags ?? []).forEach((tag) => {
      (peopleByTag.get(tag) ?? []).forEach((person) => ids.add(person.id ?? person));
    });
    return ids.size;
  };

  const persist = (updated) => {
    setCategories(updated);
    saveExpertiseTags(updated);
  };

  const updateCategory = (updated) => persist(categories.map((c) => c.id === updated.id ? updated : c));

  // window.confirm was doing this job: unstyled, unscannable, and dismissed by
  // reflex. An empty category also deleted with no prompt at all.
  const deleteCategory = (id) => {
    const cat = categories.find((c) => c.id === id);
    if (cat) setPendingDeleteCategory(cat);
  };

  const confirmDeleteCategory = () => {
    const cat = pendingDeleteCategory;
    if (!cat) return;
    persist(categories.filter((c) => c.id !== cat.id));
    setPendingDeleteCategory(null);
  };

  const addCategory = () => {
    const trimmed = newCategoryName.trim();
    if (!trimmed) return;
    persist([...categories, { id: `cat-${Date.now()}`, name: trimmed, tags: [] }]);
    setNewCategoryName('');
    setShowNewCategory(false);
  };

  const totalTags = categories.reduce((sum, c) => sum + c.tags.length, 0);

  return (
    <div>
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-slate-800 dark:text-zinc-100">Expertise Tags</h2>
          <p className="text-sm text-slate-500 dark:text-zinc-400 mt-0.5">
            Company-wide expertise tags organized by category. Employees select from these when editing their profiles.
          </p>
          <p className="text-xs text-slate-400 dark:text-zinc-500 mt-1">{categories.length} categories &middot; {totalTags} total tags</p>
        </div>
        <button
          onClick={() => setShowNewCategory(true)}
          className="flex items-center gap-1.5 px-3 py-2 bg-teal-500 text-white rounded-lg text-sm font-medium hover:bg-teal-600 transition-colors flex-shrink-0"
        >
          <PlusIcon className="w-4 h-4" />
          Add Category
        </button>
      </div>

      {showNewCategory && (
        <div className="bg-white dark:bg-zinc-900 border border-teal-200 dark:border-teal-900 rounded-xl p-4 mb-5 flex gap-2">
          <input
            type="text"
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') addCategory(); if (e.key === 'Escape') { setShowNewCategory(false); setNewCategoryName(''); } }}
            placeholder="New category name..."
            autoFocus
            className="flex-1 px-3 py-2 text-sm border border-slate-200 dark:border-zinc-700 rounded-lg bg-white dark:bg-zinc-800 dark:text-zinc-100 dark:placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-teal-400/40 focus:border-teal-400"
          />
          <button onClick={addCategory} disabled={!newCategoryName.trim()} className="px-4 py-2 bg-teal-500 text-white rounded-lg text-sm font-medium hover:bg-teal-600 disabled:opacity-40 transition-colors">Create</button>
          <button onClick={() => { setShowNewCategory(false); setNewCategoryName(''); }} className="px-3 py-2 text-slate-500 dark:text-zinc-400 hover:text-slate-700 dark:hover:text-zinc-200 text-sm">Cancel</button>
        </div>
      )}

      {categories.length === 0 ? (
        <div className="py-16 text-center border border-dashed border-slate-200 dark:border-zinc-700 rounded-xl">
          <TagIcon className="w-8 h-8 text-slate-300 dark:text-zinc-600 mx-auto mb-2" />
          <p className="text-sm text-slate-400 dark:text-zinc-500">No categories yet. Add one to get started.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {categories.map((cat) => (
            <CategoryCard key={cat.id} category={cat} onUpdate={updateCategory} onDelete={deleteCategory} countsByTag={countsByTag} />
          ))}
        </div>
      )}

      <ConfirmDialog
        open={Boolean(pendingDeleteCategory)}
        title={`Delete the "${pendingDeleteCategory?.name}" category?`}
        body={
          pendingDeleteCategory?.tags?.length
            ? `Removes ${pendingDeleteCategory.tags.length} tag${pendingDeleteCategory.tags.length === 1 ? '' : 's'}. `
              + `${categoryHolders(pendingDeleteCategory)} people hold at least one of them — they keep it on their own profile, `
              + 'but the capability disappears from the Expertise browser and the skills facet in Filters for everyone.'
            : 'The category is empty, so nothing else changes.'
        }
        confirmLabel="Delete category"
        confirmPhrase={pendingDeleteCategory?.tags?.length > 4 ? pendingDeleteCategory.name : undefined}
        onConfirm={confirmDeleteCategory}
        onCancel={() => setPendingDeleteCategory(null)}
      />
    </div>
  );
}

function TrophyIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
      <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
      <path d="M4 22h16" />
      <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
      <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
      <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
    </svg>
  );
}

// The emoji are gone from the award objects themselves; these rows exist to fire a
// celebration at each tier, so they carry the year each tier is reached.
const TENURE_BADGES = SERVICE_AWARDS;

function BadgeTesting({ currentUserId }) {
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationBadge, setCelebrationBadge] = useState(null);
  const [celebrationBadgeType, setCelebrationBadgeType] = useState('tenure');
  const [celebrationShowConfetti, setCelebrationShowConfetti] = useState(true);
  const [earnedBadges, setEarnedBadges] = useState(() => getUserBadges(currentUserId));
  const [pendingReset, setPendingReset] = useState(false);

  const refreshBadges = () => setEarnedBadges(getUserBadges(currentUserId));

  const triggerCelebration = (badge, type, confetti = true) => {
    setCelebrationBadge(badge);
    setCelebrationBadgeType(type);
    setCelebrationShowConfetti(confetti);
    setShowCelebration(true);
  };

  const awardAndCelebrate = (badgeId) => {
    const badge = awardBadge(currentUserId, badgeId);
    if (badge) {
      triggerCelebration(badge, 'achievement', true);
      refreshBadges();
    }
  };

  // Reset All wiped every earned badge on click. It only touches the signed-in
  // admin's own record, but "irreversible" and "one click" should not meet.
  const handleReset = () => setPendingReset(true);

  const confirmReset = () => {
    resetBadges(currentUserId);
    refreshBadges();
    setPendingReset(false);
  };

  const badgesByCategory = getBadgesByCategory();
  const earnedCount = earnedBadges.earned?.length || 0;
  const totalCount = Object.keys(ACHIEVEMENT_BADGES).length;

  return (
    <>
      <div className="space-y-6">
        <div>
          <div className="flex items-center justify-between gap-4 mb-1.5">
            <div className="flex items-center gap-2.5">
              <h2 className="text-lg font-semibold text-slate-800 dark:text-zinc-100">Badge Testing</h2>
              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-slate-100 dark:bg-zinc-800 text-slate-500 dark:text-zinc-400 border border-slate-200 dark:border-zinc-700">
                {earnedCount} / {totalCount} earned
              </span>
            </div>
            <button
              onClick={handleReset}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors flex-shrink-0 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 border border-red-200 dark:border-red-900/40"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Reset All
            </button>
          </div>
          <p className="text-sm text-slate-500 dark:text-zinc-400">
            Preview and test badge celebrations and gamification features.
          </p>
        </div>

        {/* Tenure Badges */}
        <div className="bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-700 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 dark:border-zinc-800">
            <h3 className="text-sm font-semibold text-slate-700 dark:text-zinc-300">Tenure Badges</h3>
            <p className="text-xs text-slate-400 dark:text-zinc-500 mt-0.5">Click any badge to preview its celebration animation.</p>
          </div>
          <div className="p-4 grid grid-cols-4 sm:grid-cols-7 gap-2">
            {TENURE_BADGES.map((badge) => (
              <button
                key={badge.tier}
                onClick={() => triggerCelebration(badge, 'tenure', true)}
                className="flex flex-col items-center gap-1 p-3 rounded-xl bg-slate-50 dark:bg-zinc-800 hover:bg-teal-50 dark:hover:bg-teal-900/20 hover:border-teal-200 border border-transparent transition-colors"
                title={badge.description}
              >
                {/* The preview shows the object that actually ships, so testing a
                    celebration also proves the tile renders at its real tier. */}
                <ServiceTile years={badge.years} size={40} />
                <span className="text-[10px] text-ink-3 text-center leading-tight">{badge.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Achievement Badges by category */}
        {Object.entries(badgesByCategory).map(([category, badges]) => (
          <div key={category} className="bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-700 rounded-xl overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 dark:border-zinc-800 flex items-center gap-2">
              <span>
                {category === 'profile' && '👤'}
                {category === 'interaction' && '🤝'}
                {category === 'engagement' && '🎯'}
                {category === 'special' && '✨'}
              </span>
              <h3 className="text-sm font-semibold text-slate-700 dark:text-zinc-300 capitalize">{category} Badges</h3>
            </div>
            <div className="divide-y divide-slate-100 dark:divide-zinc-800">
              {badges.map((badge) => {
                const isEarned = earnedBadges.earned?.includes(badge.id);
                return (
                  <div
                    key={badge.id}
                    className={`flex items-center justify-between px-5 py-3.5 ${isEarned ? 'bg-teal-50/50 dark:bg-teal-900/10' : ''}`}
                  >
                    <div className="flex items-center gap-3">
                      <Medallion size={40} metal="brand" state={isEarned ? 'earned' : 'locked'}>
                        {badge.icon}
                      </Medallion>
                      <div>
                        <p className={`text-sm font-medium ${isEarned ? 'text-teal-700 dark:text-teal-400' : 'text-slate-700 dark:text-zinc-300'}`}>
                          {badge.name}
                          {isEarned && <span className="ml-2 text-teal-500 text-xs">✓ earned</span>}
                        </p>
                        <p className="text-xs text-slate-400 dark:text-zinc-500">{badge.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => triggerCelebration(badge, 'achievement', true)}
                        className="p-2 rounded-lg text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors"
                        title="Preview with confetti"
                      >🎉</button>
                      <button
                        onClick={() => triggerCelebration(badge, 'achievement', false)}
                        className="p-2 rounded-lg text-slate-400 hover:bg-slate-50 dark:hover:bg-zinc-800 transition-colors"
                        title="Preview without confetti"
                      >👁️</button>
                      {!isEarned && (
                        <button
                          onClick={() => awardAndCelebrate(badge.id)}
                          className="p-2 rounded-lg text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors"
                          title="Award badge"
                        >➕</button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <ConfirmDialog
        open={pendingReset}
        title="Clear your earned badges?"
        body={`Removes all ${earnedCount} achievement${earnedCount === 1 ? '' : 's'} from your own record so you can test the celebrations again. Nobody else is affected, and tenure badges come back on their own because they are derived from your hire date.`}
        confirmLabel="Clear badges"
        onConfirm={confirmReset}
        onCancel={() => setPendingReset(false)}
      />

      <BadgeCelebration
        isOpen={showCelebration}
        onClose={() => setShowCelebration(false)}
        badge={celebrationBadge}
        employeeName="Test User"
        badgeType={celebrationBadgeType}
        showConfetti={celebrationShowConfetti}
      />
    </>
  );
}

function PaletteIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22a1 1 0 0 1-1-1v-1a2 2 0 0 1 2-2h1a2 2 0 0 0 2-2 2 2 0 0 1 2-2h1.7a2 2 0 0 0 2-1.6c.2-.9.3-1.9.3-2.9C22 5.6 17.5 2 12 2 6.5 2 2 6.5 2 12s4.5 10 10 10Z" />
      <circle cx="6.5" cy="11.5" r="1.2" fill="currentColor" stroke="none" />
      <circle cx="9.5" cy="6.5" r="1.2" fill="currentColor" stroke="none" />
      <circle cx="14.5" cy="6.5" r="1.2" fill="currentColor" stroke="none" />
      <circle cx="17.5" cy="11.5" r="1.2" fill="currentColor" stroke="none" />
    </svg>
  );
}

function PhotoIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <path d="M21 15l-5-5L5 21" />
    </svg>
  );
}

/**
 * Photo cache, on the mockup's `.card`/`.srow` pattern.
 *
 * Three things the old panel got wrong, all of them about state.
 *
 * "Backend not configured" replaced the whole panel, so an admin could not see
 * what the panel would even do once it was configured, and nothing said what the
 * app falls back to meanwhile. It is now a `.notice` band above a read-only panel
 * that states the fallback: per-browser Graph fetches.
 *
 * A running sync had no stop. 739 photos at ten concurrent uploads is minutes of
 * work, and leaving the page does not unwind the loop in an SPA — the only exit
 * was closing the tab. There is a Stop now, and it is honest about in-flight
 * uploads finishing.
 *
 * Nightly automatic sync is shown as a real row whether it is available or not,
 * rather than being absent. The original mockup copy said it was blocked on an
 * Application-level Graph permission that IT had not granted; as of July 27, 2026,
 * `User.Read.All` is granted, so the row must stop implying the feature is missing.
 */
function PhotoCacheSync({ employees }) {
  const { getAccessToken } = useAuth();
  const API_BASE = import.meta.env.VITE_API_BASE_URL;
  const [status, setStatus] = useState('idle');
  const [progress, setProgress] = useState({ done: 0, total: 0, skipped: 0 });
  const [result, setResult] = useState(null);
  const abortRef = useRef(null);

  // A sync outlives this panel's mount otherwise — unmounting stops the queue.
  useEffect(() => () => abortRef.current?.abort(), []);

  const configured = Boolean(API_BASE);
  const loading = employees.length === 0;
  const pct = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;

  const handleSync = async () => {
    const controller = new AbortController();
    abortRef.current = controller;
    setStatus('syncing');
    setProgress({ done: 0, total: 0, skipped: 0 });
    setResult(null);
    try {
      const token = await getAccessToken();
      const res = await syncPhotosToBackend(
        employees,
        token,
        (done, total, skipped) => setProgress({ done, total, skipped }),
        { signal: controller.signal },
      );
      setResult(res);
      setStatus(res.stopped ? 'stopped' : 'done');
    } catch (err) {
      console.error('Photo sync failed:', err);
      setStatus('error');
    } finally {
      abortRef.current = null;
    }
  };

  return (
    <div className="space-y-3.5">
      <div>
        <h3 className="text-[16px] font-bold text-ink mb-[3px]">Photo cache</h3>
        <p className="text-[12.5px] text-ink-3 leading-[1.5]">
          Uploads every employee photo from Microsoft Graph to Azure Storage once, so each
          browser loads them from the cache instead of fetching 739 photos itself.
        </p>
      </div>

      {/* .notice — the state a settings page must not hide. Names the setting to
          change and what the app does until then. */}
      {!configured && (
        <div className="flex items-start gap-2.5 rounded-card border border-amber-300/70 dark:border-amber-800/60
                        bg-amber-50/80 dark:bg-amber-950/25 px-3.5 py-3">
          <span className="text-[13px] leading-none mt-[2px]" aria-hidden>&#9888;</span>
          <p className="text-[12px] text-amber-900 dark:text-amber-300 leading-[1.55]">
            <b className="font-bold">Backend not configured.</b>{' '}
            Set <code className="font-mono text-[11.5px] px-1 rounded bg-amber-100/80 dark:bg-amber-900/40">VITE_API_BASE_URL</code>{' '}
            to enable photo caching. Until then this section is read-only and the directory
            falls back to per-browser Graph fetches — slower on first load, and it costs every
            user a Graph call per person.
          </p>
        </div>
      )}

      <SettingsCard
        title="Cache status"
        description={
          configured
            ? 'Already-cached photos are skipped, so a second run only picks up new joiners.'
            : 'Read-only until the backend is configured.'
        }
      >
        {status === 'syncing' ? (
          <div className="px-3.5 py-3 border-b border-line last:border-b-0">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <p className="text-[12.5px] font-semibold text-ink">
                  Running — <span className="font-mono">{progress.done}</span> of{' '}
                  <span className="font-mono">{progress.total || '…'}</span>
                </p>
                <p className="text-[11.5px] text-ink-3 leading-[1.5] mt-[3px]">
                  {progress.skipped > 0 && <>{progress.skipped} already cached and skipped · </>}
                  safe to leave this page — the sync keeps running until you stop it or close the tab.
                </p>
              </div>
              <button
                onClick={() => abortRef.current?.abort()}
                className="h-[30px] px-3 text-[12px] font-semibold rounded-ctl border border-line-strong
                           bg-surface text-ink-2 hover:bg-surface-hover flex-none"
              >
                Stop
              </button>
            </div>
            <div
              className="h-1.5 mt-2.5 rounded-full bg-surface-2 overflow-hidden"
              role="progressbar"
              aria-valuenow={pct}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Photo sync progress"
            >
              <div className="h-full bg-accent rounded-full transition-[width] duration-300" style={{ width: `${pct}%` }} />
            </div>
          </div>
        ) : (
          <SettingRow
            label="Sync photos to cache"
            help={
              status === 'done' && result
                ? `Complete — ${result.synced} uploaded, ${result.skipped} already cached, ${result.total} people in the directory.`
                : status === 'stopped' && result
                  ? `Stopped after ${result.attempted} of ${result.attempted + result.remaining}. ${result.synced} uploaded — what got through stays cached, so running again resumes rather than starting over.`
                  : status === 'error'
                    ? 'Last run failed. The console has the failing request; cached photos from earlier runs are unaffected.'
                    : loading
                      ? 'Waiting for the directory to finish loading.'
                      : `Runs against all ${employees.length} people. Ten uploads at a time; a full run takes a few minutes.`
            }
            badge={
              status === 'error' ? (
                <span className="text-[9.5px] font-bold uppercase tracking-[0.04em] px-1.5 py-0.5 rounded border
                                 border-red-300 dark:border-red-900 bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-400">
                  Failed
                </span>
              ) : status === 'done' ? (
                <span className="text-[9.5px] font-bold uppercase tracking-[0.04em] px-1.5 py-0.5 rounded border
                                 border-accent-line bg-accent-soft text-accent">
                  Done
                </span>
              ) : null
            }
          >
            <button
              onClick={handleSync}
              disabled={!configured || loading}
              title={!configured ? 'Requires VITE_API_BASE_URL' : loading ? 'Directory still loading' : undefined}
              className="h-[30px] px-3 text-[12px] font-semibold rounded-ctl bg-accent text-[var(--on-accent)]
                         disabled:opacity-45 disabled:cursor-not-allowed"
            >
              {status === 'idle' ? 'Sync now' : 'Run again'}
            </button>
          </SettingRow>
        )}

        <SettingRow
          label="Nightly automatic sync"
          help="Application-level Microsoft Graph access is granted. The backend can run the nightly photo sync without a signed-in user, using `User.Read.All` on the function app."
          badge={
            <span className="text-[9.5px] font-bold uppercase tracking-[0.04em] px-1.5 py-0.5 rounded border
                             border-accent-line bg-accent-soft text-accent">
              Ready
            </span>
          }
        >
          <SettingToggle checked={true} onChange={() => {}} label="Nightly automatic sync — backend Graph permission granted" />
        </SettingRow>
      </SettingsCard>
    </div>
  );
}

export function Settings({ employees, photos, currentUserId, allSkills, onDirectoryRefresh, initialTab = null, peopleAccessReviewFilter = null }) {
  const [activeTab, setActiveTab] = useState(initialTab || 'users');

  // Grouped by blast radius. Everything here used to sit at one level, so
  // "Badge Testing" — which awards real badges to a real account and has a
  // Reset All — read as equal in weight to a display preference.
  //
  // Badge Testing is a development tool: it writes to the badge record the whole
  // firm can see. Hidden unless VITE_SHOW_BADGE_TESTING is set, per the spec's
  // recommendation to gate it behind a flag.
  const showBadgeTesting = import.meta.env.VITE_SHOW_BADGE_TESTING === 'true';

  // Mockup 5's .railnav groups, with counts so the rail says how much is behind
  // each item before you click it.
  const tabGroups = [
    {
      label: 'Organization',
      meta: `${employees.length} users`,
      tabs: [
        { id: 'users', label: 'People & access', icon: UserIcon, count: employees.length },
        { id: 'config', label: 'App configuration', icon: SlidersIcon },
        { id: 'tags', label: 'Expertise taxonomy', icon: TagIcon },
        { id: 'award-colors', label: 'Service Award Colors', icon: PaletteIcon },
        { id: 'photos', label: 'Photo cache', icon: PhotoIcon },
      ],
    },
    ...(showBadgeTesting
      ? [{
          label: 'Developer',
          hint: 'Writes real badge state',
          tabs: [{ id: 'badges', label: 'Badge tools', icon: TrophyIcon, dev: true }],
        }]
      : []),
  ];

  const tabs = tabGroups.flatMap((group) => group.tabs);
  // Badge Testing can vanish behind its flag; without this the pane would render
  // empty with a tab highlighted that no longer exists.
  const activeTabExists = tabs.some((tab) => tab.id === activeTab);
  useEffect(() => {
    if (!activeTabExists) setActiveTab(tabs[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabExists]);

  useEffect(() => {
    if (initialTab) setActiveTab(initialTab);
  }, [initialTab]);

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-8">
        <PageHeader title="Settings" subtitle="Admin-only configuration for PeopleVUE." />
      </div>

      {/* ── .setpane — 212px rail + body ─────────────────────────────────
          Mockup 5. These were horizontal tabs, which forces every group onto one
          scrolling line and leaves a group heading nowhere to live. A vertical rail
          carries the grouping, counts per item, and a Dev pill on tools that write
          real state. */}
      <div className="grid md:grid-cols-[212px_1fr] rounded-card border border-line bg-surface shadow-e1 overflow-hidden min-w-0">
        <nav className="border-b md:border-b-0 md:border-r border-line bg-surface-2 p-2 pb-4 min-w-0">
          {tabGroups.map((group, groupIndex) => (
            <div key={group.label}>
              {groupIndex > 0 && <div className="h-px bg-line mx-2.5 my-2" />}
              <div className="flex items-baseline gap-2 px-2.5 pb-1.5 pt-1">
                <span className="text-[9.5px] font-bold uppercase tracking-[0.13em] text-ink-4">
                  {group.label}
                </span>
                {group.meta && (
                  <span className="ml-auto font-mono text-[9px] text-ink-4">{group.meta}</span>
                )}
              </div>
              {group.tabs.map(({ id, label, icon: Icon, count, dev }) => (
                <button
                  key={id}
                  onClick={() => setActiveTab(id)}
                  {...(activeTab === id ? { 'aria-current': 'true' } : {})}
                  className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-[7px] text-[12.5px] font-semibold
                    text-left transition-colors
                    ${activeTab === id
                      ? 'bg-surface text-accent shadow-e1'
                      : 'text-ink-2 hover:bg-surface hover:text-ink'}`}
                >
                  <Icon className="w-3.5 h-3.5 flex-none" />
                  <span className="truncate">{label}</span>
                  {typeof count === 'number' && (
                    <span className={`ml-auto font-mono text-[10.5px] flex-none ${activeTab === id ? 'text-accent' : 'text-ink-4'}`}>
                      {count}
                    </span>
                  )}
                  {dev && (
                    <span className="ml-auto flex-none text-[9px] font-bold uppercase tracking-[0.06em]
                                     px-1.5 py-0.5 rounded border border-amber-300 dark:border-amber-800
                                     bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300">
                      Dev
                    </span>
                  )}
                </button>
              ))}
              {group.hint && (
                <p className="px-2.5 pt-2 text-[10.5px] leading-[1.5] text-ink-4">
                  {group.hint}. Hidden unless <span className="font-mono">VITE_SHOW_BADGE_TESTING</span> is set.
                </p>
              )}
            </div>
          ))}
        </nav>

        <div className="min-w-0 p-5">

        {activeTab === 'users' && (
          <PeopleAccess
            employees={employees}
            photos={photos}
            currentUserId={currentUserId}
            onDirectoryRefresh={onDirectoryRefresh}
            reviewFilter={peopleAccessReviewFilter}
          />
        )}
        {activeTab === 'config' && <AppConfiguration />}
        {activeTab === 'award-colors' && <AwardMetalsSettings />}
        {activeTab === 'photos' && <PhotoCacheSync employees={employees} />}
        {activeTab === 'tags' && <ExpertiseTags allSkills={allSkills} />}
        {activeTab === 'badges' && <BadgeTesting currentUserId={currentUserId} />}
        </div>
      </div>
    </div>
  );
}
